/**
 * Every deterministic sentence the report builder says. M10 / S10-01.
 *
 * **WORDING LIVES IN ONE PLACE SO IT CAN BE TESTED AS WORDING**, the same
 * convention Sol follows. A count sentence assembled inline inside JSX is one a
 * test can only reach by rendering a whole screen, and "3 capture" shipped that
 * way is the kind of defect that survives every structural test and is caught
 * by a person on a phone.
 *
 * **THERE IS NO AI ANYWHERE IN REPORTS** (PT §17). Nothing here is generated,
 * suggested or summarized — these are fixed strings with a number in them.
 */

/**
 * `1 capture` · `3 captures` · `0 captures`.
 *
 * **ZERO IS PLURAL AND IS NOT SPECIAL-CASED.** English pluralizes zero, the
 * design export's own label does exactly this, and a bespoke "no captures"
 * branch would be a second sentence to keep in step for a state where the
 * control carrying it is disabled anyway.
 */
export function describeCaptureCount(count: number): string {
  return `${count} capture${count === 1 ? '' : 's'}`;
}

/** The primary action: `Continue with 3 captures`. */
export function continueLabel(count: number): string {
  return `Continue with ${describeCaptureCount(count)}`;
}

/** `Continue with 3 captures` — the primary action on the Capture step. */

/**
 * What a photo tile says beside its tick — M10 / S10-02.
 *
 * **IT NAMES THE PHOTO'S REAL STATE, NEVER A GUESS.** A photo still uploading
 * must not sit silently in a chooser looking identical to one the server has;
 * Brandon is deciding what leaves Crewlink, and "this one is not there yet" is
 * exactly the kind of thing PT §20 forbids the product to overstate.
 */
export function describeTile(view: {
  readonly photoClass: 'before' | 'after';
  readonly state: 'pending' | 'uploading' | 'failed' | 'delivered';
}): string {
  const name = view.photoClass === 'before' ? 'Before' : 'After';
  if (view.state === 'delivered') return name;
  if (view.state === 'uploading') return `${name} · uploading`;
  if (view.state === 'pending') return `${name} · waiting to upload`;
  return `${name} · did not upload`;
}

/** The viewer's mono label, matching the Capture screen's. */
export function viewerLabel(view: {
  readonly photoClass: 'before' | 'after';
  readonly state: 'pending' | 'uploading' | 'failed' | 'delivered';
}): string {
  return describeTile(view).toUpperCase();
}

/**
 * Said when a Capture has no photos of one class.
 *
 * **AN HONEST ABSENCE, NOT A WARNING.** A Capture may contribute zero photos to
 * a report (Jerry's decision, 2026-08-18), so this states what is there and
 * implies nothing about whether the report is valid.
 */
export function describeNoPhotos(photoClass: 'before' | 'after'): string {
  return `No ${photoClass === 'before' ? 'Before' : 'After'} photos on this Capture.`;
}
